import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AsyncSelector,
  Button,
  Panel,
  PanelSection,
  toast,
  toListRequest,
  toPagedSearchParams,
  type AsyncSearchParams,
} from '@/shared/ui';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import {
  getGetVenuaPlannerProductQueryKey,
  getVenuaPlannerDestinationSuggestions,
  useGetVenuaPlannerProduct,
  useUpdateVenuaPlannerProductDestinations,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { plannerQueryKeys } from '../../plannerApi';
import { productDetailAgenda } from './productDetailAgenda';

interface ProductDetailPageProps {
  layerId: string;
}

interface DestinationOption {
  destinationId: number;
  name: string;
  code?: string | null;
}

/**
 * Detail produktu. V této iteraci obsahuje pouze sekci Destinace (uložené v legacy Product_Destination,
 * předvyplněné z projektu při založení produktu).
 */
export function ProductDetailPage({ layerId }: ProductDetailPageProps) {
  const { t } = useTranslation('planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const { open } = useAgendaParams(productDetailAgenda, layerId);
  const productId = open.productId;

  const productQuery = useGetVenuaPlannerProduct(productId);
  const loaded = productQuery.data;

  const [destinations, setDestinations] = useState<readonly DestinationOption[]>([]);

  useEffect(() => {
    if (!loaded) return;
    setDestinations(
      loaded.destinations.map((d) => ({
        destinationId: d.destinationId,
        name: d.name,
        code: d.code,
      }))
    );
  }, [loaded]);

  const updateMutation = useUpdateVenuaPlannerProductDestinations();

  const searchDestinations = useCallback(
    async (
      params: AsyncSearchParams
    ): Promise<{ items: DestinationOption[]; totalCount: number }> => {
      const response = await getVenuaPlannerDestinationSuggestions(
        toListRequest(params, undefined)
      );
      return {
        items: response.items.map((item) => ({
          destinationId: item.destinationId,
          name: item.name,
          code: item.code,
        })),
        totalCount: response.totalCount,
      };
    },
    []
  );

  const destinationIds = useMemo(() => destinations.map((d) => d.destinationId), [destinations]);

  const closeForm = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const handleSave = useCallback(() => {
    updateMutation.mutate(
      { productId, data: { destinationIds } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getGetVenuaPlannerProductQueryKey(productId),
          });
          void queryClient.invalidateQueries({ queryKey: plannerQueryKeys.tree() });
          toast({ severity: 'success', description: t('productDetail.toast.saved') });
          closeForm();
        },
        onError: () => {
          toast({
            severity: 'error',
            title: t('productDetail.toast.errorTitle'),
            description: t('productDetail.toast.saveError'),
          });
        },
      }
    );
  }, [updateMutation, productId, destinationIds, queryClient, t, closeForm]);

  return (
    <div className="bg-background h-full min-h-0 overflow-y-auto" data-testid="product-detail">
      <div className="mx-auto w-full max-w-220 p-4">
        <Panel
          variant="filled"
          title={loaded ? loaded.name : t('productDetail.title')}
          footer={
            <div className="gap-phx-2 flex w-full items-center justify-end">
              <Button variant="ghost" size="md" onClick={closeForm}>
                {t('productDetail.actions.cancel')}
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSave}
                loading={updateMutation.isPending}
                disabled={updateMutation.isPending}
                data-testid="product-detail-save-button"
              >
                {t('productDetail.actions.save')}
              </Button>
            </div>
          }
        >
          {productQuery.isLoading ? (
            <p className="text-phx-text-muted text-phx-sm">{t('productDetail.loading')}</p>
          ) : productQuery.isError ? (
            <Alert severity="error" title={t('productDetail.loadErrorTitle')}>
              {t('productDetail.loadErrorDescription')}
            </Alert>
          ) : (
            <PanelSection title={t('productDetail.destinations.sectionLabel')}>
              <AsyncSelector<DestinationOption>
                mode="multi"
                label={t('productDetail.destinations.label')}
                helperText={t('productDetail.destinations.help')}
                selected={destinations}
                getKey={(item) => item.destinationId}
                columns={[
                  {
                    id: 'name',
                    header: t('destinationSelector.columns.name'),
                    cell: (item) => (item.code ? `${item.name} (${item.code})` : item.name),
                  },
                ]}
                search={searchDestinations}
                queryKey={(params) => [
                  'venua-planner',
                  'destinations',
                  toPagedSearchParams(params),
                ]}
                onChange={(next) => setDestinations(next)}
                placeholder={t('productDetail.destinations.placeholder')}
                testId="product-detail-destinations"
              />
            </PanelSection>
          )}
        </Panel>
      </div>
    </div>
  );
}
